import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import { ArticleFieldsType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import useUserData from 'hooks/useUserData';
import { useMemo } from 'react';
import useArticleDefaultFields from './useArticleDefaultFields';
import { ConfigEntryType } from 'config/config';
import { uniqBy } from 'lodash';
import {
  ARTICLE_DETAILS_TOP_LEVEL_FIELDS,
  ARTICLE_FORM_TOP_LEVEL_FIELDS,
  MANDATORY_APPLICATION_BASED_EDIT_SUGGESTION_FIELDS,
} from '../constants';

function useArticleInputFields({
  articleFieldsType = ArticleFieldsType.POST,
  isEditing,
  isHelpCenterCollection,
}: {
  articleFieldsType?: ArticleFieldsType;
  isEditing: boolean;
  isHelpCenterCollection?: boolean;
}) {
  const { companyId } = useUserData();
  const articleFormConfigName = useMemo(
    () =>
      [ArticleFieldsType.POST, ArticleFieldsType.NOTES].includes(articleFieldsType)
        ? CONFIGS.ARTICLE_FIELDS.configName
        : articleFieldsType === ArticleFieldsType.QUESTION
          ? CONFIGS.QUESTION_INPUT_FIELDS.configName
          : articleFieldsType === ArticleFieldsType.SUGGESTION
            ? CONFIGS.SUGGESTION_INPUT_FIELDS.configName
            : articleFieldsType === ArticleFieldsType.LESSON
              ? CONFIGS.RETROSPECTIVE_INPUT_FIELDS.configName
              : CONFIGS.HELP_CENTER_INPUT_FIELDS.configName,
    [articleFieldsType],
  );

  const {
    entries: articleConfigFields,
    isLoading: isLoadingArticleConfigFields,
    isFetched: isFetchedArticleConfigFields,
  } = useConfigQuery({
    companyId,
    configName: articleFormConfigName,
    shouldFetch: articleFieldsType !== ArticleFieldsType.HELP_CENTER,
  });
  const { defaultArticleFields } = useArticleDefaultFields({ articleFieldsType });
  const articleFields = useMemo(() => {
    let fields = uniqBy([...articleConfigFields, ...defaultArticleFields], 'entryCode').filter(
      (field) => field.isActive,
    );

    // Order title, description, attachments fields at top when they aren't coming from config
    const configFields = articleConfigFields.map((field) => field.entryCode);
    const missingTopLevelFields = ARTICLE_FORM_TOP_LEVEL_FIELDS[articleFieldsType]?.filter(
      (field) => !configFields.includes(field),
    );
    if (missingTopLevelFields?.length) {
      const topLevelFields: ConfigEntryType[] = [];
      missingTopLevelFields.forEach((fieldName) => {
        const missingField = fields.find((field) => field.entryCode === fieldName);
        if (missingField) {
          topLevelFields.push(missingField);
        }
      });
      fields = [...topLevelFields, ...fields];
    }

    // Hide Collections field when editing article.
    if (isEditing) {
      // Suggestion form mandatory fields
      if (articleFieldsType === ArticleFieldsType.SUGGESTION) {
        fields.push(...MANDATORY_APPLICATION_BASED_EDIT_SUGGESTION_FIELDS);
      }
    }

    // Only the title and playListArticleId fields are required for the Help Center collection
    if (articleFieldsType === ArticleFieldsType.HELP_CENTER && isHelpCenterCollection) {
      fields = fields.filter((field) => ['title', 'playListArticleIds'].includes(field.entryCode));
    }

    fields = uniqBy(fields, 'entryCode');

    return fields;
  }, [articleConfigFields, isEditing, defaultArticleFields, articleFieldsType]);

  const articleFieldsToRender = useMemo(() => {
    let fields = uniqBy([...articleConfigFields, ...defaultArticleFields], 'entryCode').filter(
      (field) => field.isActive,
    );

    const topLevelFields: ConfigEntryType[] = [];
    ARTICLE_DETAILS_TOP_LEVEL_FIELDS[articleFieldsType]?.forEach((fieldName) => {
      const topLevelField = fields.find((field) => field.entryCode === fieldName);
      if (topLevelField) {
        topLevelFields.push(topLevelField);
      }
    });

    // Suggestion form mandatory fields to render
    if (articleFieldsType === ArticleFieldsType.SUGGESTION) {
      fields.push(...MANDATORY_APPLICATION_BASED_EDIT_SUGGESTION_FIELDS);
    }

    fields = uniqBy([...topLevelFields, ...fields], 'entryCode');

    return fields;
  }, [articleFieldsType, articleConfigFields, defaultArticleFields]);

  return {
    articleFields,
    articleFieldsToRender,
    articleFormConfigName,
    defaultArticleFields,
    isFetchedArticleConfigFields,
    isLoadingArticleConfigFields,
  };
}

export default useArticleInputFields;
